import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Dumbbell, Users } from "lucide-react";
import { useAuth, homeForRole } from "@/lib/auth";
import { usePrefs, useT } from "@/lib/prefs";
import { LANGUAGE_META, CURRENCY_META } from "@/lib/i18n";
import { Logo, Button, Field } from "@/components/brand";
import { cn } from "@/lib/utils";

export default function Register() {
  const t = useT();
  const { register } = useAuth();
  const { language, currency, setPrefs } = usePrefs();
  const [role, setRole] = useState("member");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register(form.name.trim(), form.email.trim().toLowerCase(), form.password, role, { language, currency });
      window.location.assign(homeForRole(u.role));
    } catch (err) {
      toast.error(err.message || "Registration failed");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-10 bg-white" data-testid="register-screen">
      <div className="w-full max-w-md">
        <Link to="/"><Logo size={34} /></Link>
        <h1 className="font-display text-4xl text-ink tracking-tight mt-8">{t("auth.join")} Sofit</h1>
        <p className="font-body text-mutedgrey mt-1">{t("auth.joinSub")}</p>

        <div className="font-body font-semibold text-sm text-ink mt-6 mb-2">{t("auth.iWantTo")}</div>
        <div className="grid grid-cols-2 gap-3">
          {[["member", Dumbbell, t("auth.train"), t("auth.trainSub")], ["trainer", Users, t("auth.coach"), t("auth.coachSub")]].map(([key, Icon, label, sub]) => (
            <button key={key} type="button" data-testid={`register-role-${key}`} onClick={() => setRole(key)}
              className={cn("flex flex-col gap-1.5 p-4 rounded-xl2 border text-left transition-colors", role === key ? "border-brand bg-brand-soft2" : "border-line")}>
              <Icon size={22} className="text-brand" />
              <span className="font-display text-base text-ink">{label}</span>
              <span className="font-body text-xs text-mutedgrey">{sub}</span>
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="flex flex-col gap-4 mt-6">
          <Field label={t("auth.name")} testID="register-name" value={form.name} onChange={set("name")} placeholder="Jane Doe" required minLength={2} />
          <Field label={t("auth.email")} testID="register-email" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" required />
          <Field label={t("auth.password")} testID="register-password" type="password" value={form.password} onChange={set("password")} placeholder="At least 6 characters" required minLength={6} />

          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1.5">
              <span className="font-body font-semibold text-[13px] text-ink">{t("prefs.language")}</span>
              <select data-testid="register-language" value={language} onChange={(e) => setPrefs({ language: e.target.value }, false)}
                className="h-[52px] rounded-md border border-line bg-surface2 px-3 font-body text-[15px] text-ink">
                {LANGUAGE_META.map((l) => <option key={l.key} value={l.key}>{l.flag} {l.label}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="font-body font-semibold text-[13px] text-ink">{t("prefs.currency")}</span>
              <select data-testid="register-currency" value={currency} onChange={(e) => setPrefs({ currency: e.target.value }, false)}
                className="h-[52px] rounded-md border border-line bg-surface2 px-3 font-body text-[15px] text-ink">
                {CURRENCY_META.map((c) => <option key={c.key} value={c.key}>{c.symbol} {c.key}</option>)}
              </select>
            </label>
          </div>

          <Button testID="register-submit" type="submit" title={role === "trainer" ? t("auth.createTrainer") : t("auth.create")} loading={loading} full />
        </form>
        <p className="font-body text-sm text-mutedgrey text-center mt-6">
          {t("auth.haveAccount")} <Link to="/login" data-testid="register-to-login" className="font-semibold text-brand">{t("auth.login")}</Link>
        </p>
      </div>
    </div>
  );
}
